import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { withRouter } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Button,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import {
  GetApp,
  Assignment,
  HourglassEmpty,
  Schedule,
  TrendingUp,
  TrendingDown,
} from '@material-ui/icons';
import { StatusChip } from '../shared/StatusChip';
import {
  STATUSES,
  STATUS_LABELS,
  ROLES,
  ENTRY_CATEGORIES,
} from '../shared/constants';
import { calculateEntry } from '../shared/calc';
import { formatCategoryLabel, formatWorkDateDisplay } from '../shared/format';
import { useTimesheetTableStyles } from '../shared/tableStyles';
import { useTimesheetPageStyles } from '../shared/pageStyles';
import EngineerAutocomplete from '../shared/EngineerAutocomplete';
import { fetchTimesheetEntriesPage, PAGE_SIZE } from '../api';

const PRIMARY = '#00236f';
const ON_SURFACE = '#191c1e';
const ON_VARIANT = '#444651';
const OUTLINE = '#c5c5d3';
const SURFACE_LOWEST = '#ffffff';
const GREEN = '#2e7d32';
const RED = '#c62828';

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  exportBtn: {
    display: 'inline-flex !important',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: 40,
    minHeight: 40,
    padding: '0 20px !important',
    background: '#e0e3e5',
    color: '#444651',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontWeight: 700,
    fontSize: 12,
    lineHeight: '16px',
    '&:hover': {
      background: '#d3d6d8',
    },
    '& .MuiButton-label': {
      display: 'inline-flex !important',
      flexDirection: 'row !important',
      alignItems: 'center !important',
      whiteSpace: 'nowrap',
      padding: '0 !important',
      margin: '0 !important',
      lineHeight: '16px',
    },
  },
  exportInner: {
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  exportIcon: {
    fontSize: '16px !important',
    width: '16px !important',
    height: '16px !important',
  },
  exportLabel: {
    lineHeight: '16px',
  },
  metrics: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 16,
    marginBottom: theme.spacing(3),
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: '1fr 1fr 1fr',
    },
  },
  metricCard: {
    background: SURFACE_LOWEST,
    border: `1px solid ${OUTLINE}`,
    borderRadius: 8,
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  metricTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  metricLabel: {
    color: ON_VARIANT,
    fontSize: 16,
  },
  metricIcon: {
    color: PRIMARY,
    fontSize: 22,
  },
  metricValue: {
    fontWeight: 700,
    fontSize: 40,
    lineHeight: 1.15,
    letterSpacing: '-0.02em',
    color: ON_SURFACE,
  },
  metricFoot: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 13,
    color: ON_VARIANT,
    flexWrap: 'wrap',
  },
  trendUp: { color: GREEN, fontWeight: 600 },
  trendDown: { color: RED, fontWeight: 600 },
  filters: {
    display: 'flex',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  filter: { minWidth: 160 },
  engineerFilter: {
    minWidth: 200,
    maxWidth: 280,
    flex: '1 1 200px',
  },
  clearBtn: {
    height: 40,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontWeight: 700,
    fontSize: 12,
    color: ON_VARIANT,
    // Force visible border over MUI outlined styles.
    border: `1px solid ${OUTLINE} !important`,
    borderColor: `${OUTLINE} !important`,
    '&:hover': {
      border: `1px solid ${OUTLINE} !important`,
      borderColor: `${OUTLINE} !important`,
    },
    '&.Mui-disabled': {
      border: `1px solid ${OUTLINE} !important`,
      borderColor: `${OUTLINE} !important`,
      opacity: 0.85,
    },
  },
}));

const emptySummary = {
  activeThisMonth: 0,
  activeDeltaPct: null,
  pendingCount: 0,
  monthHours: 0,
};

const entryHours = entry => {
  const calc = entry.calc || calculateEntry(entry);
  return (
    (calc.standardHours || 0) + (calc.timedHours || 0) + (calc.kitHours || 0)
  );
};

const monthKey = date => `${date.getFullYear()}-${date.getMonth()}`;

const buildSummaryFromEntries = entries => {
  const now = new Date();
  const thisMonth = monthKey(now);
  const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonth = monthKey(lastMonthDate);

  let activeThisMonth = 0;
  let activeLastMonth = 0;
  let pendingCount = 0;
  let monthHours = 0;

  (entries || []).forEach(entry => {
    const d = new Date(`${entry.workDate}T12:00:00`);
    const key = monthKey(d);
    const isActive =
      entry.status === STATUSES.DRAFT || entry.status === STATUSES.PENDING;

    if (entry.status === STATUSES.PENDING) pendingCount += 1;
    if (key === thisMonth) {
      monthHours += entryHours(entry);
      if (isActive) activeThisMonth += 1;
    }
    if (key === lastMonth && isActive) activeLastMonth += 1;
  });

  let activeDeltaPct = null;
  if (activeLastMonth > 0) {
    activeDeltaPct = Math.round(
      ((activeThisMonth - activeLastMonth) / activeLastMonth) * 100,
    );
  } else if (activeThisMonth > 0) {
    activeDeltaPct = 100;
  }

  return {
    activeThisMonth,
    activeDeltaPct,
    pendingCount,
    monthHours: Math.round(monthHours * 10) / 10,
  };
};

const TimesheetLogs = ({
  currentUser,
  users,
  basePath,
  history,
  appname,
  fmId,
}) => {
  const classes = useStyles();
  const tableClasses = useTimesheetTableStyles();
  const pageClasses = useTimesheetPageStyles();
  const engineers = users.filter(u => u.role === ROLES.ENGINEER);
  const scopedEngineers = useMemo(
    () =>
      engineers.filter(e =>
        currentUser.role === ROLES.SYSTEM_ADMIN
          ? true
          : e.assignedFmId === currentUser.id,
      ),
    [engineers, currentUser.role, currentUser.id],
  );
  const [status, setStatus] = useState('all');
  const [category, setCategory] = useState('all');
  const [engineerId, setEngineerId] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [summary, setSummary] = useState(emptySummary);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [listError, setListError] = useState(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const loadPage = useCallback(
    async (offset, append) => {
      if (!appname) return;
      const reqId = ++requestIdRef.current;
      if (append) setLoadingMore(true);
      else {
        setLoading(true);
        setListError(null);
      }

      try {
        const page = await fetchTimesheetEntriesPage({
          appname,
          fmId: fmId || undefined,
          engineerId: engineerId !== 'all' ? engineerId : undefined,
          status: status !== 'all' ? status : undefined,
          category: category !== 'all' ? category : undefined,
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
          search: debouncedSearch || undefined,
          offset,
          limit: PAGE_SIZE,
          includeSummary: append ? undefined : '1',
        });

        if (reqId !== requestIdRef.current) return;

        setRows(prev => (append ? [...prev, ...page.entries] : page.entries));
        setTotal(page.total);
        setHasMore(page.hasMore);
        if (!append) {
          setSummary(
            page.summary
              ? { ...emptySummary, ...page.summary }
              : buildSummaryFromEntries(page.entries),
          );
        }
      } catch (err) {
        if (reqId !== requestIdRef.current) return;
        console.error(err);
        setListError(err.message || 'Failed to load entries');
        if (!append) {
          setRows([]);
          setTotal(0);
          setHasMore(false);
        }
      } finally {
        if (reqId === requestIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [
      appname,
      fmId,
      engineerId,
      status,
      category,
      fromDate,
      toDate,
      debouncedSearch,
    ],
  );

  useEffect(() => {
    loadPage(0, false);
  }, [loadPage]);

  const onTableScroll = event => {
    if (!hasMore || loadingMore || loading) return;
    const el = event.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 96) {
      loadPage(rows.length, true);
    }
  };

  const engineerName = id => {
    const eng = engineers.find(u => u.id === id);
    return eng ? eng.name : id;
  };

  const hasActiveFilters =
    status !== 'all' ||
    category !== 'all' ||
    engineerId !== 'all' ||
    !!fromDate ||
    !!toDate ||
    !!search.trim();

  const clearFilters = () => {
    setSearch('');
    setStatus('all');
    setCategory('all');
    setEngineerId('all');
    setFromDate('');
    setToDate('');
  };

  const displayRows = useMemo(() => {
    if (!rows.length) return rows;
    return [...rows].sort((a, b) => {
      const aStamp =
        a.submittedAt || a.updatedAt || a.createdAt || a.workDate || '';
      const bStamp =
        b.submittedAt || b.updatedAt || b.createdAt || b.workDate || '';
      if (aStamp === bStamp) return 0;
      return aStamp < bStamp ? 1 : -1;
    });
  }, [rows]);

  return (
    <Box>
      <Box className={classes.header}>
        <div>
          <Typography
            className={pageClasses.pageTitle}
            style={{ textTransform: 'uppercase' }}
          >
            Timesheet Logs
          </Typography>
          <Typography color="textSecondary">
            {currentUser.role === ROLES.SYSTEM_ADMIN
              ? 'All Engineers | System Admin view'
              : `Assigned engineers for ${currentUser.name}`}
          </Typography>
        </div>
        <Button
          className={classes.exportBtn}
          onClick={() => history.push(`${basePath}/export`)}
        >
          <span className={classes.exportInner}>
            <GetApp className={classes.exportIcon} />
            <span className={classes.exportLabel}>Export Excel</span>
          </span>
        </Button>
      </Box>

      <Box className={classes.metrics}>
        <Box className={classes.metricCard}>
          <Box className={classes.metricTop}>
            <Typography className={classes.metricLabel}>
              Active Timesheets
            </Typography>
            <Assignment className={classes.metricIcon} />
          </Box>
          <Typography className={classes.metricValue}>
            {summary.activeThisMonth}
          </Typography>
          <Typography className={classes.metricFoot} component="div">
            {summary.activeDeltaPct == null ? (
              <span>No prior-month comparison yet</span>
            ) : summary.activeDeltaPct >= 0 ? (
              <>
                <TrendingUp style={{ fontSize: 16, color: GREEN }} />
                <span className={classes.trendUp}>
                  +{summary.activeDeltaPct}%
                </span>
                <span> vs last month</span>
              </>
            ) : (
              <>
                <TrendingDown style={{ fontSize: 16, color: RED }} />
                <span className={classes.trendDown}>
                  {summary.activeDeltaPct}%
                </span>
                <span> vs last month</span>
              </>
            )}
          </Typography>
        </Box>

        <Box className={classes.metricCard}>
          <Box className={classes.metricTop}>
            <Typography className={classes.metricLabel}>
              Pending Approval
            </Typography>
            <HourglassEmpty className={classes.metricIcon} />
          </Box>
          <Typography className={classes.metricValue}>
            {summary.pendingCount}
          </Typography>
          <Typography className={classes.metricFoot} component="div">
            {summary.pendingCount === 0 ? (
              <span>All caught up</span>
            ) : (
              <>
                <TrendingDown style={{ fontSize: 16, color: RED }} />
                <span className={classes.trendDown}>
                  {summary.pendingCount}
                </span>
                <span> require attention</span>
              </>
            )}
          </Typography>
        </Box>

        <Box className={classes.metricCard}>
          <Box className={classes.metricTop}>
            <Typography className={classes.metricLabel}>
              Total Hours (Month)
            </Typography>
            <Schedule className={classes.metricIcon} />
          </Box>
          <Typography className={classes.metricValue}>
            {summary.monthHours}
          </Typography>
          <Typography className={classes.metricFoot}>
            Logged across team entries this month
          </Typography>
        </Box>
      </Box>

      <Box className={classes.filters}>
        <TextField
          className={classes.filter}
          label="Search"
          variant="outlined"
          size="small"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <TextField
          select
          className={classes.filter}
          label="Status"
          variant="outlined"
          size="small"
          value={status}
          onChange={e => setStatus(e.target.value)}
        >
          <MenuItem value="all">All</MenuItem>
          {Object.values(STATUSES).map(s => (
            <MenuItem key={s} value={s}>
              {STATUS_LABELS[s]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          className={classes.filter}
          label="Category"
          variant="outlined"
          size="small"
          value={category}
          onChange={e => setCategory(e.target.value)}
        >
          <MenuItem value="all">All</MenuItem>
          {ENTRY_CATEGORIES.map(c => (
            <MenuItem key={c.value} value={c.value}>
              {c.label}
            </MenuItem>
          ))}
        </TextField>
        <EngineerAutocomplete
          className={classes.engineerFilter}
          engineers={scopedEngineers}
          value={engineerId}
          onChange={setEngineerId}
          label="Engineer"
          allLabel="All"
        />
        <TextField
          label="From"
          type="date"
          variant="outlined"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={fromDate}
          onChange={e => setFromDate(e.target.value)}
          inputProps={{ lang: 'en-GB' }}
        />
        <TextField
          label="To"
          type="date"
          variant="outlined"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={toDate}
          onChange={e => setToDate(e.target.value)}
          inputProps={{ lang: 'en-GB' }}
        />
        <Button
          variant="outlined"
          className={classes.clearBtn}
          onClick={clearFilters}
          disabled={!hasActiveFilters}
        >
          Clear filters
        </Button>
      </Box>

      {listError ? (
        <Typography color="error" style={{ marginBottom: 12 }}>
          {listError}
        </Typography>
      ) : null}

      <TableContainer
        component={Paper}
        className={tableClasses.tablePaper}
        onScroll={onTableScroll}
      >
        {loading && rows.length === 0 ? (
          <Box p={4} display="flex" justifyContent="center">
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Table className={tableClasses.table} stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Engineer</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography color="textSecondary">
                        No matching logs.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  displayRows.map(entry => (
                    <TableRow
                      key={entry.id}
                      hover
                      style={{ cursor: 'pointer' }}
                      onClick={() => history.push(`${basePath}/${entry.id}`)}
                    >
                      <TableCell>
                        {formatWorkDateDisplay(entry.workDate)}
                      </TableCell>
                      <TableCell>{engineerName(entry.engineerId)}</TableCell>
                      <TableCell>{formatCategoryLabel(entry)}</TableCell>
                      <TableCell>{entry.customerName || '—'}</TableCell>
                      <TableCell>
                        <StatusChip status={entry.status} />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          color="primary"
                          onClick={e => {
                            e.stopPropagation();
                            history.push(`${basePath}/${entry.id}`);
                          }}
                        >
                          Review
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            {rows.length > 0 ? (
              <Typography className={tableClasses.scrollHint}>
                {loadingMore
                  ? `Loading more… (${rows.length} of ${total})`
                  : hasMore
                    ? `Showing ${rows.length} of ${total} — scroll for more`
                    : `Showing all ${total} entries`}
              </Typography>
            ) : null}
          </>
        )}
      </TableContainer>
    </Box>
  );
};

export default withRouter(TimesheetLogs);
